import { AfterViewInit, Directive, ElementRef, OnDestroy } from '@angular/core';

/**
 * Maps every Material Icon ligature name used in this project to its
 * Font Awesome Pro Sharp-Solid equivalent.
 *
 * To add a new icon: find the FA name at https://fontawesome.com/icons
 * and append   materialName: 'fa-name'   here.
 */
const ICON_MAP: Record<string, string> = {
  // ── A ──────────────────────────────────────────────────────────
  account_balance_wallet:         'wallet',
  account_circle:                 'circle-user',
  add:                            'plus',
  add_circle:                     'circle-plus',
  add_location:                   'location-plus',
  archive:                        'box-archive',
  arrow_back:                     'arrow-left',
  arrow_back_ios:                 'chevron-left',
  arrow_downward:                 'arrow-down',
  arrow_drop_down:                'chevron-down',
  arrow_drop_down_circle:         'circle-chevron-down',
  arrow_forward_ios:              'chevron-right',
  arrow_upward:                   'arrow-up',
  article:                        'file-lines',
  assignment:                     'clipboard',
  attach_file:                    'paperclip',
  attach_money:                   'dollar-sign',
  auto_awesome:                   'sparkles',
  // ── B ──────────────────────────────────────────────────────────
  badge:                          'id-badge',
  business:                       'building',
  // ── C ──────────────────────────────────────────────────────────
  calendar_today:                 'calendar',
  calendar_view_month:            'calendar-days',
  calendar_view_week:             'calendar-week',
  cancel:                         'circle-xmark',
  chat:                           'message',
  check:                          'check',
  check_box:                      'square-check',
  check_circle:                   'circle-check',
  chevron_left:                   'chevron-left',
  chevron_right:                  'chevron-right',
  circle:                         'circle',
  close:                          'xmark',
  cloud_download:                 'cloud-arrow-down',
  cloud_sync:                     'cloud-arrow-up',
  cloud_upload:                   'cloud-arrow-up',
  content_copy:                   'copy',
  content_paste:                  'paste',
  crop_free:                      'crop',
  // ── D ──────────────────────────────────────────────────────────
  delete:                         'trash-can',
  delete_forever:                 'trash-xmark',
  description:                    'file-lines',
  description_outlined:           'file-lines',
  do_not_disturb_on:              'circle-minus',
  download:                       'arrow-down-to-line',
  download_for_offline:           'arrow-down-to-line',
  drag_handle:                    'grip-lines',
  drag_indicator:                 'grip-dots',
  draw:                           'pen-nib',
  dynamic_form:                   'table-list',
  // ── E ──────────────────────────────────────────────────────────
  edit:                           'pen',
  email:                          'envelope',
  error:                          'circle-exclamation',
  error_outline:                  'circle-exclamation',
  expand_more:                    'chevron-down',
  // ── F ──────────────────────────────────────────────────────────
  file_download:                  'file-arrow-down',
  file_upload:                    'file-arrow-up',
  flag:                           'flag',
  folder_open:                    'folder-open',
  folder_zip:                     'file-zip',
  food_bank:                      'utensils',
  forum:                          'comments',
  // ── G ──────────────────────────────────────────────────────────
  gps_fixed:                      'location-crosshairs',
  grid_view:                      'grid-2',
  group:                          'users',
  // ── H ──────────────────────────────────────────────────────────
  help_outline:                   'circle-question',
  history:                        'clock-rotate-left',
  hourglass_empty:                'hourglass',
  // ── I ──────────────────────────────────────────────────────────
  image:                          'image',
  info:                           'circle-info',
  info_outline:                   'circle-info',
  insert_drive_file:              'file',
  // Custom aliases used in templates for semantically-colored icons
  file_excel:                     'file-excel',
  file_pdf_red:                   'file-pdf',
  inventory_2:                    'boxes-stacking',
  // ── K ──────────────────────────────────────────────────────────
  keyboard_arrow_right:           'chevron-right',
  // ── L ──────────────────────────────────────────────────────────
  language:                       'globe',
  link_off:                       'link-slash',
  list:                           'list',
  lock_clock:                     'clock',
  logout:                         'right-from-bracket',
  // ── M ──────────────────────────────────────────────────────────
  mail:                           'envelope',
  manage_accounts:                'user-gear',
  menu:                           'bars',
  menu_open:                      'bars',
  mood:                           'face-smile',
  more_vert:                      'ellipsis-vertical',
  // ── N ──────────────────────────────────────────────────────────
  notifications:                  'bell',
  notifications_active:           'bell',
  // ── O ──────────────────────────────────────────────────────────
  open_in_new:                    'arrow-up-right-from-square',
  // ── P ──────────────────────────────────────────────────────────
  palette:                        'palette',
  pause:                          'pause',
  payments:                       'money-check',
  pending:                        'clock',
  people:                         'users',
  person:                         'user',
  person_add:                     'user-plus',
  person_search:                  'user-magnifying-glass',
  phone:                          'phone',
  picture_as_pdf:                 'file-pdf',
  play_arrow:                     'play',
  play_circle:                    'circle-play',
  playlist_add:                   'square-plus',
  preview:                        'eye',
  psychology:                     'brain',
  publish:                        'upload',
  // ── R ──────────────────────────────────────────────────────────
  radio_button_checked:           'circle-dot',
  receipt_long:                   'receipt',
  refresh:                        'arrows-rotate',
  remove:                         'minus',
  remove_red_eye:                 'eye',
  replay:                         'rotate-left',
  request_quote:                  'file-invoice-dollar',
  restore:                        'clock-rotate-left',
  // ── S ──────────────────────────────────────────────────────────
  sms:                            'message',
  save:                           'floppy-disk',
  schedule:                       'clock',
  science:                        'flask',
  search:                         'magnifying-glass',
  send:                           'paper-plane',
  send_and_archive:               'paper-plane',
  settings:                       'gear',
  settings_outlined:              'gear',
  smart_toy:                      'robot',
  sort:                           'arrow-up-arrow-down',
  star:                           'star',
  star_border:                    'star',
  stop:                           'square',
  storage:                        'database',
  sync:                           'arrows-rotate',
  // ── T ──────────────────────────────────────────────────────────
  table_chart:                    'chart-bar',
  table_view:                     'table',
  text_fields:                    'input-text',
  text_snippet:                   'file-lines',
  timeline:                       'timeline',
  tune:                           'sliders',
  // ── U ──────────────────────────────────────────────────────────
  unarchive:                      'box-open',
  upload:                         'upload',
  upload_file:                    'file-arrow-up',
  // ── V ──────────────────────────────────────────────────────────
  vertical_split:                 'columns',
  view_list:                      'list',
  view_quilt:                     'grip',
  visibility:                     'eye',
  visibility_off:                 'eye-slash',
  // ── W ──────────────────────────────────────────────────────────
  warning:                        'triangle-exclamation',
  warning_amber:                  'triangle-exclamation',
};

/**
 * Global directive that intercepts every `<mat-icon>` element (except those
 * using the [svgIcon] input) and replaces the Material Icons font glyph with
 * the corresponding Font Awesome Pro Sharp-Solid icon.
 *
 * This means NO individual HTML template needs to be changed —
 * the migration is applied automatically, project-wide.
 *
 * For dynamic content ({{ expr }}), a MutationObserver watches for text
 * changes and re-applies the conversion automatically.
 */
@Directive({
    selector: 'mat-icon:not([svgIcon])',
    standalone: false
})
export class FaIconDirective implements AfterViewInit, OnDestroy {
  private observer?: MutationObserver;
  private busy = false;

  constructor(private el: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    this.apply();

    // Watch for text-content changes (handles {{ interpolated }} icon names)
    this.observer = new MutationObserver(() => {
      if (!this.busy) this.apply();
    });
    this.observer.observe(this.el.nativeElement, {
      characterData: true,
      childList: true,
      subtree: true,
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private apply(): void {
    this.busy = true;
    try {
      const host = this.el.nativeElement;

      // Read icon name from text (ignore any <i> children we already injected)
      const iconName = Array.from(host.childNodes)
        .filter(n => n.nodeType === Node.TEXT_NODE)
        .map(n => n.textContent?.trim() ?? '')
        .join('')
        .trim();

      if (!iconName) return;

      const faName = ICON_MAP[iconName];
      if (!faName) {
        // Unmapped icon — leave Material icon visible as fallback
        return;
      }

      // Check if we already injected the right icon (avoid flicker)
      const existing = host.querySelector(`i.fa-${faName}`);
      if (existing) return;

      // Suppress Material Icons font rendering
      host.style.fontFamily = 'none';
      host.style.overflow = 'hidden';

      // Remove all text nodes and previous injected icons
      Array.from(host.childNodes)
        .filter(n => n.nodeType === Node.TEXT_NODE || (n as HTMLElement).tagName === 'I')
        .forEach(n => n.remove());

      // Inject FA icon element
      const i = document.createElement('i');
      i.className = `fa-regular fa-${faName}`;
      i.style.cssText = 'font-size:inherit;line-height:inherit;display:flex;align-items:center;justify-content:center;width:100%;height:100%;';
      host.appendChild(i);
    } finally {
      // Release the guard after the current microtask completes
      setTimeout(() => { this.busy = false; }, 0);
    }
  }
}
